import { StepperSelectionEvent } from '@angular/cdk/stepper';
import { Component, OnInit, OnDestroy, ViewChild, ChangeDetectorRef } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatStepper } from '@angular/material/stepper';
import { ActivatedRoute } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import { ReplaySubject, takeUntil } from 'rxjs';

import { delayFor } from 'app/core/utilities/async-utils';
import { VideoChatService } from 'app/modules/social/services/videochat.service';
import { SocialPageService } from 'app/modules/social/social-page/services/social-page.service';
import { RoomSizeInfo } from 'app/modules/social/types/room';
import { SnackBarComponent, SnackBarData } from 'app/shared/snackbar/snackbar.component';
import { Loggable } from 'app/utils/logging/loggable';
import Social from 'entity/Social';
import SocialTheme from 'entity/SocialTheme';

import { ScootaversePageService } from '../../services/scootaverse-page.service';

@Component({
  selector: 'create-room-page',
  templateUrl: './create-room-page.component.html',
  styleUrls: ['./create-room-page.component.scss'],
  standalone: false,
})
export class CreateRoomPageComponent extends Loggable implements OnInit, OnDestroy {
  private destroyed$ = new ReplaySubject<boolean>(1);

  protected step = 0;
  protected roomName?: string;
  protected roomSize?: RoomSizeInfo;
  protected playlistId?: string;
  protected theme?: SocialTheme;
  protected selectedIndex = 0;
  public scootaverseId: string | undefined = undefined;
  protected roomId?: string;
  private social?: Social;
  public isCreateFlow = true;
  protected showLoader = false;

  protected hasBackStepButton = true;

  protected unSavedChanges = false;
  protected confirmMessage =
    'Your room is not yet created! If you leave now you will lose your work, but we will continue to process your image and it will be available in your asset library when complete.';

  @ViewChild('stepper') public stepper!: MatStepper;

  public constructor(
    private socialPageService: SocialPageService,
    private route: ActivatedRoute,
    private scootaversePageService: ScootaversePageService,
    private videoChatService: VideoChatService,
    private _snackBar: MatSnackBar,
    private toastrService: ToastrService,
    private changeDetectorRef: ChangeDetectorRef,
  ) {
    super();
    this.setDisplayName('CreateRoomPageComponent');
  }

  public async ngOnInit(): Promise<void> {
    this.scootaverseId = this.route.snapshot.paramMap.get('id') || undefined;
    if (!this.scootaverseId) {
      void this.scootaversePageService.redirectToHome();
    }
    this.roomId = this.route.snapshot.paramMap.get('room-id') || undefined;

    if (this.roomId) {
      this.showLoader = true;
      this.isCreateFlow = false;

      const editStep = this.route.snapshot.paramMap.get('step-id') || undefined;
      if (editStep) {
        this.step = Number(editStep);
        this.hasBackStepButton = false;
      }

      await this.videoChatService
        .getSocialPromise(this.roomId, false)
        .then((social) => {
          this.social = social;
          this.roomName = social.name;
          this.roomSize = {
            capacity: social.maxParticipants?.toString() ?? '',
            size: social.startingHeight?.toString() ?? '',
          };
          this.playlistId = social.playlistId;
          this.theme = social.theme;
          this.changeDetectorRef.markForCheck();
          this.showLoader = false;
        })
        .catch((err: unknown) => {
          this.error('Error loading social information: ', err);
          this.showLoader = false;
        });
    }
  }

  public ngOnDestroy(): void {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }

  public selectedName(event: string): void {
    this.roomName = event;
    if (this.roomId) {
      this.editRoom();
    } else {
      this.step = 1;
    }
  }

  public async selectedRoomSize(event: RoomSizeInfo): Promise<void> {
    this.roomSize = event;
    if (this.roomId) {
      this.editRoom();
    } else {
      this.step = 2;
      // wait stepper animation
      await delayFor(100);
      this.stepper.selectedIndex = 1;
    }
  }

  public async selectedMusicPlaylist(event: string): Promise<void> {
    this.playlistId = event;
    if (this.roomId) {
      this.editRoom();
    } else {
      this.step = 3;
      // wait stepper animation
      await delayFor(100);
      this.stepper.selectedIndex = 2;
    }
  }

  public async selectedBackground(event: SocialTheme): Promise<void> {
    this.theme = event;
    if (this.roomId) {
      this.editRoom();
    } else {
      this.step = 4;
      // wait stepper animation
      await delayFor(100);
      this.stepper.selectedIndex = 3;
    }
  }

  public backStep(): void {
    if (this.step > 0) {
      if (this.unSavedChanges) {
        const confirm = window.confirm(this.confirmMessage);

        if (!confirm) {
          return;
        }
      }
      this.step = this.step - 1;
    }
  }

  public get completedFirstStep(): boolean {
    return this.step > 1 && !!this.roomSize && !!this.roomName;
  }

  public get completedSecondStep(): boolean {
    return this.step > 2 && !!this.playlistId;
  }

  public get completedLastStep(): boolean {
    return this.step > 3 && !!this.theme;
  }

  public async confirmCreateRoomResp(event: boolean): Promise<void> {
    if (event) {
      if (this.roomId) {
        this.editRoom();
      } else {
        await this.createRoom();
      }
    } else {
      this.step = 3;
    }
  }

  private editRoom(): void {
    if (this.social) {
      this.social = {
        ...this.social,
        name: this.roomName,
        themeId: this.theme?.socialThemeId,
        theme: this.theme,
        playlistId: this.playlistId,
        startingWidth: Number(this.roomSize?.size),
        startingHeight: Number(this.roomSize?.size),
        maxParticipants: Number(this.roomSize?.capacity),
      };

      this.videoChatService
        .updateSocial(this.social)
        .pipe(takeUntil(this.destroyed$))
        .subscribe({
          next: () => {
            this.openSnackBar();

            this.scootaversePageService.onEditRoom(this.social, this.scootaverseId).catch((e) => {
              this.error('Error redirecting to room - ', e);
            });
          },

          error: (error: unknown) => {
            this.error('Failed to update social', error);
            this.toastrService.error('Error updating room info! Try again later.');
            this.scootaversePageService.onEnterScootaverse(this.scootaverseId ?? '').catch((e) => {
              this.error('Error redirecting to room - ', e);
            });
          },
        });
    }
  }

  private async createRoom(): Promise<void> {
    const socialTemplate = {
      name: this.roomName,
      themeId: this.theme?.socialThemeId,
      theme: this.theme,
      playlistId: this.playlistId,
      startingWidth: Number(this.roomSize?.size),
      startingHeight: Number(this.roomSize?.size),
      maxParticipants: Number(this.roomSize?.capacity),
      recurring: true,
    };

    const room = {
      buttonClickedLocation: 'New Creating Room Flow',
      socialTemplate,
      socialRoomSetId: this.scootaverseId,
    };

    await this.socialPageService
      .createRoomAllInfo(room)
      .then(() => {
        this.openSnackBar();
        void this.changeScootaverseName();
      })
      .catch((error) => {
        this.error('Error when creating background theme', error);
      });
  }

  private openSnackBar(): void {
    const _data: SnackBarData = {
      message: `Scootaverse is ready to use`,
      type: 'success',
      icon: 'APPROVE_ICON',
    };
    this._snackBar.openFromComponent(SnackBarComponent, {
      duration: 500,
      horizontalPosition: 'center',
      verticalPosition: 'bottom',
      panelClass: 'custom-snackbar',
      data: _data,
    });
  }

  private async changeScootaverseName() {
    const socialRoomSet = await this.videoChatService.getSocialRoomSetPromise(this.scootaverseId ?? '');

    if (!socialRoomSet) {
      return;
    }

    if (socialRoomSet.socials?.length === 1) {
      await this.scootaversePageService
        .updateSocialRoomSet({
          socialRoomSetId: this.scootaverseId,
          name: this.roomName,
        })
        .catch((err) => this.error(err))
        .finally(() => this.scootaversePageService.updateSelectedScootaverse());
    }
  }

  public backToScootaversePage(): void {
    if (this.scootaverseId) {
      if (this.roomId) {
        this.scootaversePageService.onEditRoom(this.social, this.scootaverseId).catch((e) => {
          this.error('Error redirecting to room - ', e);
        });
      } else {
        this.scootaversePageService.onEnterScootaverse(this.scootaverseId).catch((e) => {
          this.error('Error redirecting to room - ', e);
        });
      }
    }
  }

  public selectionChange(e: StepperSelectionEvent): void {
    if (this.unSavedChanges) {
      const confirm = window.confirm(this.confirmMessage);

      if (!confirm) {
        return;
      }
    }

    switch (e.selectedIndex) {
      case 0: {
        this.step = 1;
        break;
      }
      case 1: {
        this.step = 2;
        break;
      }
      case 2: {
        this.step = 3;
        break;
      }
    }
  }

  protected changeStep(step: number) {
    if (!this.isCreateFlow) {
      return;
    }
    this.stepper.selectedIndex = step;
    this.step = step;
  }
}
